import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { PortalAuthLayout } from '@/components/portal/PortalAuthLayout';
import { usePortalForgotPassword } from '@/hooks/portal/usePortalAuth';

export const Route = createFileRoute('/(portal)/portal/forgot')({
  component: PortalForgotPage,
});

const schema = z.object({ email: z.string().email('Enter a valid email address') });
type Values = z.infer<typeof schema>;

function PortalForgotPage() {
  const forgot = usePortalForgotPassword();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: Values) {
    await forgot.mutateAsync(values.email).catch(() => undefined);
  }

  if (forgot.isSuccess) {
    return (
      <PortalAuthLayout
        title="Check your email"
        description="If that address has a portal account, a password-reset link is on its way."
        footer={
          <Link to="/portal/login" className="font-medium text-foreground underline">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">The link expires in 1 hour.</p>
      </PortalAuthLayout>
    );
  }

  return (
    <PortalAuthLayout
      title="Reset your password"
      description="We'll email you a link to set a new password."
      footer={
        <Link to="/portal/login" className="font-medium text-foreground underline">
          Back to sign in
        </Link>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={forgot.isPending}>
            {forgot.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Send reset link
          </Button>
        </form>
      </Form>
    </PortalAuthLayout>
  );
}
